/**
 * Manager Dashboard
 *
 * Displays real-time data from the mcp-appointments and mcp-crm services.
 * Features:
 *   - KPI stat cards (appointments, leads)
 *   - Quick-action grid for common workflows
 *   - Lead pipeline status breakdown
 *   - Appointment status overview
 *   - Today's appointments table
 *   - Recent leads list
 *   - Upcoming appointments table
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, Row, Col, Table, Tag, Button, Flex, Skeleton, Badge, theme } from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  SolutionOutlined,
  ClusterOutlined,
  DashboardOutlined,
  ThunderboltOutlined,
  ArrowRightOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import isToday from 'dayjs/plugin/isToday';

import { DashboardPage, type DashboardStatCardConfig } from '@/components/dashboard/DashboardPage';
import { QuickActionCard } from '@/components/dashboard/QuickActionCard';
import { DashboardBreakdownRow } from '@/components/manage-dashboard/DashboardBreakdownRow';
import { SectionTitle } from '@/components/dashboard/SectionTitle';
import { EmptyState } from '@/components/dashboard/EmptyState';
import { DashboardRecentLeadRow } from '@/components/manage-dashboard/DashboardRecentLeadRow';
import { useManagerDashboardData } from '@/hooks/dashboard/useManagerDashboardData';
import { useAppointmentColumns } from '@/hooks/dashboard/useAppointmentColumns';
import type { Lead, LeadStatus } from '@/api/mcp-crm/interfaces';
import type { Appointment } from '@/api/mcp-appointments/interfaces';

dayjs.extend(relativeTime);
dayjs.extend(isToday);

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

export const Route = createFileRoute('/manage/')({
  component: ManagerDashboard
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ManagerDashboard() {
  const { t } = useTranslation(['dashboard', 'manage', 'common']);
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const {
    leadStats,
    leadPipeline,
    leadPipelineMax,
    recentLeads,
    leadsLoading,
    leadsError,
    appointmentStats,
    todayAppointments,
    upcomingAppointments,
    appointmentsLoading,
    appointmentsError,
    resources,
    isLoading,
    refetchAll
  } = useManagerDashboardData();
  const appointmentColumns = useAppointmentColumns(resources);

  /** Bar colour for a lead status, matching its tag colour. */
  const leadStatusBarColor: Record<LeadStatus, string> = {
    NEW: token.colorInfo,
    QUALIFIED: token.colorWarning,
    DISQUALIFIED: token.colorTextQuaternary,
    CONVERTED: token.colorSuccess
  };

  // =========================================================================
  // Stat cards config
  // =========================================================================

  const statCards: DashboardStatCardConfig[] = [
    {
      key: 'totalAppointments',
      label: t('dashboard:manager.stats.totalAppointments'),
      value: appointmentStats.total,
      icon: <CalendarOutlined />,
      color: token.colorPrimary,
      loading: appointmentsLoading,
      onClick: () => navigate({ to: '/manage/calendar' })
    },
    {
      key: 'todaysAppointments',
      label: t('dashboard:manager.stats.todaysAppointments'),
      value: appointmentStats.upcomingToday,
      icon: <ClockCircleOutlined />,
      color: token.colorSuccess,
      loading: appointmentsLoading,
      onClick: () => navigate({ to: '/manage/calendar' })
    },
    {
      key: 'totalLeads',
      label: t('dashboard:manager.stats.totalLeads'),
      value: leadStats.total,
      icon: <SolutionOutlined />,
      color: token.purple6,
      loading: leadsLoading,
      onClick: () => navigate({ to: '/manage/leads' })
    },
    {
      key: 'newLeads',
      label: t('dashboard:manager.stats.newLeads'),
      value: leadStats.new,
      icon: <UserAddOutlined />,
      color: token.colorInfo,
      loading: leadsLoading,
      onClick: () => navigate({ to: '/manage/leads' })
    }
  ];

  // =========================================================================
  // Quick actions config
  // =========================================================================

  const quickActions = [
    {
      title: t('dashboard:manager.quickActions.scheduleAppointment'),
      description: t('dashboard:manager.quickActions.scheduleAppointmentDesc'),
      icon: <PlusOutlined />,
      color: token.colorPrimary,
      path: '/manage/calendar' as const
    },
    {
      title: t('dashboard:manager.quickActions.viewCalendar'),
      description: t('dashboard:manager.quickActions.viewCalendarDesc'),
      icon: <CalendarOutlined />,
      color: token.colorSuccess,
      path: '/manage/calendar' as const
    },
    {
      title: t('dashboard:manager.quickActions.manageLeads'),
      description: t('dashboard:manager.quickActions.manageLeadsDesc'),
      icon: <SolutionOutlined />,
      color: token.purple6,
      path: '/manage/leads' as const
    },
    {
      title: t('dashboard:manager.quickActions.manageResources'),
      description: t('dashboard:manager.quickActions.manageResourcesDesc'),
      icon: <ClusterOutlined />,
      color: token.colorWarning,
      path: '/manage/company-resources' as const
    }
  ];

  // =========================================================================
  // Appointment status overview data
  // =========================================================================

  const appointmentStatusOverview = [
    { key: 'scheduled', label: t('dashboard:manager.appointmentOverview.scheduled'), count: appointmentStats.scheduled, color: token.colorInfo },
    { key: 'confirmed', label: t('dashboard:manager.appointmentOverview.confirmed'), count: appointmentStats.confirmed, color: token.colorSuccess },
    { key: 'completed', label: t('dashboard:manager.appointmentOverview.completed'), count: appointmentStats.completed, color: token.colorTextTertiary },
    { key: 'cancelled', label: t('dashboard:manager.appointmentOverview.cancelled'), count: appointmentStats.cancelled, color: token.colorError },
    { key: 'noShow', label: t('dashboard:manager.appointmentOverview.noShow'), count: appointmentStats.noShow, color: token.colorWarning }
  ];

  /** Renders an appointments table, or a skeleton / empty state, for a dashboard card. */
  const renderAppointmentsTable = (rows: Appointment[], emptyTitle: string, emptyHint: string) => {
    if (appointmentsLoading) return <Skeleton active paragraph={{ rows: 3 }} />;
    if (rows.length === 0) return <EmptyState title={emptyTitle} hint={emptyHint} />;
    return (
      <Table
        columns={appointmentColumns}
        dataSource={rows}
        rowKey="id"
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    );
  };

  const cardStyle = { height: '100%' };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <DashboardPage
      icon={<DashboardOutlined />}
      title={t('dashboard:manager.title')}
      description={t('dashboard:manager.welcome')}
      refreshLabel={t('dashboard:manager.refresh')}
      onRefresh={refetchAll}
      refreshing={isLoading}
      errorTitle={t('dashboard:manager.servicesUnreachable')}
      errorMessages={[
        appointmentsError && t('dashboard:manager.failedAppointments'),
        leadsError && t('dashboard:manager.failedLeads')
      ]}
      retryLabel={t('dashboard:manager.retry')}
      statCards={statCards}
    >
      {/* Quick Actions + Lead Pipeline */}
      <Row gutter={[token.marginMD, token.marginMD]} style={{ marginBottom: token.marginMD }}>
        <Col xs={24} lg={14}>
          <Card
            title={<SectionTitle icon={<ThunderboltOutlined />} title={t('dashboard:manager.quickActions.title')} />}
            style={cardStyle}
          >
            <Row gutter={[token.marginSM, token.marginSM]}>
              {quickActions.map((action) => (
                <Col xs={24} sm={12} key={action.title}>
                  <QuickActionCard showArrow
                    icon={action.icon}
                    title={action.title}
                    description={action.description}
                    color={action.color}
                    onClick={() => navigate({ to: action.path })}
                  />
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={<SectionTitle icon={<SolutionOutlined />} title={t('dashboard:manager.leadPipeline.title')} />}
            extra={
              <Button type="link" size="small" icon={<ArrowRightOutlined />} iconPlacement="end" onClick={() => navigate({ to: '/manage/leads' })}>
                {t('dashboard:manager.recentLeads.viewAll', { count: leadStats.total })}
              </Button>
            }
            style={cardStyle}
          >
            {leadsLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : leadPipeline.length > 0 ? (
              leadPipeline.map((item) => (
                <DashboardBreakdownRow
                  key={item.status}
                  label={
                    <Tag icon={item.config.icon} color={item.config.color} style={{ margin: 0 }}>
                      {t(`manage:leads.status.${item.status}`, { defaultValue: item.status })}
                    </Tag>
                  }
                  count={item.count}
                  percent={(item.count / leadPipelineMax) * 100}
                  color={leadStatusBarColor[item.status] ?? token.colorPrimary}
                />
              ))
            ) : (
              <EmptyState
                title={t('dashboard:manager.leadPipeline.noLeads')}
                hint={t('dashboard:manager.leadPipeline.noLeadsHint')}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Appointment Status Overview + Today's Appointments */}
      <Row gutter={[token.marginMD, token.marginMD]} style={{ marginBottom: token.marginMD }}>
        <Col xs={24} lg={10}>
          <Card
            title={
              <SectionTitle
                icon={<CalendarOutlined />}
                title={t('dashboard:manager.appointmentOverview.title')}
                count={appointmentsLoading ? undefined : appointmentStats.total}
              />
            }
            style={cardStyle}
          >
            {appointmentsLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : appointmentStats.total > 0 ? (
              appointmentStatusOverview.map((item) => (
                <DashboardBreakdownRow
                  key={item.key}
                  label={<Badge color={item.color} text={item.label} />}
                  count={item.count}
                  percent={(item.count / appointmentStats.total) * 100}
                  color={item.color}
                />
              ))
            ) : (
              <EmptyState
                title={t('dashboard:manager.upcomingAppointments.noAppointments')}
                hint={t('dashboard:manager.upcomingAppointments.noAppointmentsHint')}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card
            title={
              <SectionTitle
                icon={<ClockCircleOutlined />}
                title={t('dashboard:manager.todaysAppointments.title')}
                count={appointmentsLoading ? undefined : todayAppointments.length}
              />
            }
            extra={
              <Button type="link" size="small" icon={<ArrowRightOutlined />} iconPlacement="end" onClick={() => navigate({ to: '/manage/calendar' })}>
                {t('dashboard:manager.todaysAppointments.viewAll')}
              </Button>
            }
            style={cardStyle}
            styles={{ body: { paddingInline: todayAppointments.length > 0 ? 0 : undefined } }}
          >
            {renderAppointmentsTable(
              todayAppointments,
              t('dashboard:manager.todaysAppointments.noAppointments'),
              t('dashboard:manager.todaysAppointments.noAppointmentsHint')
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Leads + Upcoming Appointments */}
      <Row gutter={[token.marginMD, token.marginMD]}>
        <Col xs={24} lg={10}>
          <Card
            title={
              <SectionTitle
                icon={<SolutionOutlined />}
                title={t('dashboard:manager.recentLeads.title')}
                count={leadsLoading ? undefined : recentLeads.length}
              />
            }
            extra={
              <Button type="link" size="small" icon={<ArrowRightOutlined />} iconPlacement="end" onClick={() => navigate({ to: '/manage/leads' })}>
                {t('dashboard:manager.recentLeads.viewAll', { count: leadStats.total })}
              </Button>
            }
            style={cardStyle}
          >
            {leadsLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : recentLeads.length > 0 ? (
              <Flex vertical gap={token.marginXXS}>
                {recentLeads.map((lead: Lead) => (
                  <DashboardRecentLeadRow
                    key={lead.id}
                    lead={lead}
                    onOpen={(leadId) => navigate({ to: '/manage/leads/$leadId', params: { leadId } })}
                  />
                ))}
              </Flex>
            ) : (
              <EmptyState
                title={t('dashboard:manager.recentLeads.noLeads')}
                hint={t('dashboard:manager.recentLeads.noLeadsHint')}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card
            title={
              <SectionTitle
                icon={<CalendarOutlined />}
                title={t('dashboard:manager.upcomingAppointments.title')}
                count={appointmentsLoading ? undefined : upcomingAppointments.length}
              />
            }
            extra={
              <Button type="link" size="small" icon={<ArrowRightOutlined />} iconPlacement="end" onClick={() => navigate({ to: '/manage/calendar' })}>
                {t('dashboard:manager.upcomingAppointments.viewAll')}
              </Button>
            }
            style={cardStyle}
            styles={{ body: { paddingInline: upcomingAppointments.length > 0 ? 0 : undefined } }}
          >
            {renderAppointmentsTable(
              upcomingAppointments,
              t('dashboard:manager.upcomingAppointments.noAppointments'),
              t('dashboard:manager.upcomingAppointments.noAppointmentsHint')
            )}
          </Card>
        </Col>
      </Row>
    </DashboardPage>
  );
}
